// Generated by GitHub Copilot
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { Plus, Search, X } from 'lucide-react'
import { State, StateView, useComponentState, useCustomState } from '#core/state/State.ts'
import { UserState, useUserState } from '#core/state/UserState.ts'
import CompanyUsersApi from '#core/api/auth/CompanyUsersApi.ts'
import CompanyInvitationsApi from '#core/api/auth/CompanyInvitationsApi.ts'
import RolesApi from '#core/api/auth/RolesApi.ts'
import TeamsApi from '#core/api/operations/TeamsApi.ts'
import CostRatesApi from '#core/api/operations/CostRatesApi.ts'
import type UserCompany from '#core/entities/auth/users/UserCompany.ts'
import type CompanyInvitation from '#core/entities/auth/companies/CompanyInvitation.ts'
import type CompanyInvitationIssue from '#core/entities/auth/companies/CompanyInvitationIssue.ts'
import type RoleDetail from '#core/entities/auth/roles/RoleDetail.ts'
import type Team from '#core/entities/operations/teams/Team.ts'
import type { CompanyCostRate, UserCostRate } from '#core/entities/operations/settings/CostRate.ts'
import { showToast } from '#core/notifications/toast.ts'
import { AuthUserElevated, AuthUserLimited, PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import { ConfirmModal } from '#components/primitives/components/confirm-modal.tsx'
import { UsersTab } from './users-tab.tsx'
import { InvitationsTab } from './invitations-tab.tsx'
import { TeamsTab } from './teams-tab.tsx'
import { UserEditModal } from './user-edit-modal.tsx'
import { InviteUserModal } from './invite-user-modal.tsx'
import { TeamEditModal } from './team-edit-modal.tsx'
import { RolesTab } from './roles-tab.tsx'
import { RoleEditDrawer } from './role-edit-drawer.tsx'
import { buildInvitationAcceptUrl } from './invitation-link.ts'
import { ApiUsersState } from './api-users/api-users-state.ts'
import { ApiUsersTab } from './api-users/api-users-tab.tsx'
import { AccessRequestsState } from './access-requests/access-requests-state.ts'
import { AccessRequestsTab } from './access-requests/access-requests-tab.tsx'

export const USERS_TEAMS_TABS = ['users', 'apiUsers', 'invitations', 'accessRequests', 'teams', 'roles'] as const
export type UsersTeamsTab = (typeof USERS_TEAMS_TABS)[number]
type PageTab = UsersTeamsTab

/** Pending confirmation for a destructive action. */
export interface ConfirmAction {
  type: 'deleteUser' | 'deleteTeam' | 'revokeInvitation' | 'deleteRole'
  id: string
  name: string
}

/** Maps each confirm-action type to its i18n key prefix (under the `settings` namespace). */
const confirmKeyMap: Record<ConfirmAction['type'], string> = {
  deleteUser: 'usersTeams.deleteUser',
  deleteTeam: 'usersTeams.deleteTeam',
  revokeInvitation: 'usersTeams.revokeInvitation',
  deleteRole: 'usersTeams.deleteRole',
}

const usersApi = new CompanyUsersApi()
const invitationsApi = new CompanyInvitationsApi()
const rolesApi = new RolesApi()
const teamsApi = new TeamsApi()
const costRatesApi = new CostRatesApi()

/** Top-level page state: loads users, invitations, roles, teams and manages tab/search state. */
class UsersTeamsPageState extends State<UsersTeamsPageState> {
  activeTab: PageTab = 'users'
  searchQuery = ''
  users: UserCompany[] = []
  invitations: CompanyInvitation[] = []
  roles: RoleDetail[] = []
  teams: Team[] = []
  /** Users' own hourly rates and the company default they fall back to; loaded only for cost viewers. */
  userCostRates: UserCostRate[] = []
  defaultCostRate: CompanyCostRate | null = null
  loading = false
  error: string | null = null
  /** The most recently created/resent invitation (with its one-time token), shown in the share banner. */
  lastIssued: CompanyInvitationIssue | null = null

  // Modal visibility
  userModalOpen = false
  userModalId: string | null = null
  inviteModalOpen = false
  teamModalOpen = false
  teamModalId: string | null = null
  roleDrawerOpen = false
  roleDrawerId: string | null = null

  /** Pending destructive action awaiting confirmation. */
  confirmAction: ConfirmAction | null = null

  constructor(initialTab: PageTab = 'users') {
    super(undefined, null)
    this.activeTab = initialTab
    void this._load()
  }

  private async _load(): Promise<void> {
    this.reactTo.loading = true
    this.reactTo.error = null
    try {
      const [users, invitations, roles, teams] = await Promise.all([
        usersApi.listUsers(),
        invitationsApi.listInvitations(),
        rolesApi.listRoles(),
        // Request the full page cap so all teams show in the management list.
        teamsApi.listTeams(undefined, 200),
        this._loadCostRates(),
      ])
      this.reactTo.users = users
      this.reactTo.invitations = invitations
      this.reactTo.roles = roles
      this.reactTo.teams = teams
    } catch {
      this.reactTo.error = 'load'
    } finally {
      this.reactTo.loading = false
    }
  }

  /** Best-effort: a failure leaves the Rate column empty rather than failing the whole page. */
  private async _loadCostRates(): Promise<void> {
    const userState = StateView.getInstance(undefined, UserState.name, sessionStorage,
      () => new UserState(UserState.name, sessionStorage))
    if (!userState.hasPermission(PermissionService.COSTS, PermissionAction.READ)) return
    try {
      const [userRates, defaultRate] = await Promise.all([
        costRatesApi.listUserRates(),
        costRatesApi.getDefaultRate(),
      ])
      this.reactTo.userCostRates = userRates
      this.reactTo.defaultCostRate = defaultRate
    } catch {
      // non-critical
    }
  }

  get filteredUsers(): UserCompany[] {
    const q = this.searchQuery.toLowerCase().trim()
    if (!q) return this.users
    return this.users.filter(u =>
      u.displayName.toLowerCase().includes(q) ||
      (u.email?.toLowerCase().includes(q)) ||
      (u.role_name?.toLowerCase().includes(q)),
    )
  }

  get filteredInvitations(): CompanyInvitation[] {
    const q = this.searchQuery.toLowerCase().trim()
    if (!q) return this.invitations
    return this.invitations.filter(i =>
      i.email.toLowerCase().includes(q) ||
      (i.role_name?.toLowerCase().includes(q)) ||
      i.status.toLowerCase().includes(q),
    )
  }

  get filteredTeams(): Team[] {
    const q = this.searchQuery.toLowerCase().trim()
    if (!q) return this.teams
    return this.teams.filter(t =>
      t.name.toLowerCase().includes(q) ||
      (t.description?.toLowerCase().includes(q)),
    )
  }

  get filteredRoles(): RoleDetail[] {
    const q = this.searchQuery.toLowerCase().trim()
    if (!q) return this.roles
    return this.roles.filter(r => r.name.toLowerCase().includes(q))
  }

  async deleteUser(id: string): Promise<void> {
    try {
      await usersApi.removeUser(id)
      this.reactTo.users = this.users.filter(u => u.id !== id)
    } catch {
      showToast(i18n.t('usersTeams.deleteError', { ns: 'settings' }), 'danger')
    }
  }

  /** Requests confirmation before deleting a user. */
  requestDeleteUser(id: string): void {
    const user = this.users.find(u => u.id === id)
    this.reactTo.confirmAction = { type: 'deleteUser', id, name: user?.displayName ?? '' }
  }

  /** Requests confirmation before deleting a team. */
  requestDeleteTeam(id: string): void {
    const team = this.teams.find(t => t.id === id)
    this.reactTo.confirmAction = { type: 'deleteTeam', id, name: team?.name ?? '' }
  }

  /** Requests confirmation before revoking an invitation. */
  requestRevokeInvitation(id: string): void {
    const inv = this.invitations.find(i => i.id === id)
    this.reactTo.confirmAction = { type: 'revokeInvitation', id, name: inv?.email ?? '' }
  }

  /** Requests confirmation before deleting a role. */
  requestDeleteRole(id: string): void {
    const role = this.roles.find(r => r.id === id)
    this.reactTo.confirmAction = { type: 'deleteRole', id, name: role?.name ?? '' }
  }

  async deleteRole(id: string): Promise<void> {
    try {
      await rolesApi.deleteRole(id)
      this.reactTo.roles = this.roles.filter(r => r.id !== id)
    } catch {
      showToast(i18n.t('usersTeams.toast.deleteRoleError', { ns: 'settings' }), 'danger')
    }
  }

  /**
   * Executes the pending confirmed action. Does NOT clear `confirmAction`; the
   * primitive `ConfirmModal` closes (and we clear) via its `onClose` on success.
   */
  async executeConfirmedAction(): Promise<void> {
    const action = this.confirmAction
    if (!action) return
    switch (action.type) {
      case 'deleteUser': await this.deleteUser(action.id); break
      case 'deleteTeam': await this.deleteTeam(action.id); break
      case 'revokeInvitation': await this.revokeInvitation(action.id); break
      case 'deleteRole': await this.deleteRole(action.id); break
    }
  }

  /** Cancels the pending confirmation. */
  cancelConfirmAction(): void {
    this.reactTo.confirmAction = null
  }

  /** Enables or disables a member; the backend allows it to super users only. */
  async toggleEnabled(id: string, enabled: boolean): Promise<void> {
    try {
      await usersApi.toggleEnabled(id, enabled)
      this.reactTo.users = this.users.map(u => (u.id === id ? u.clone({ enabled }) : u))
    } catch {
      showToast(i18n.t('usersTeams.toast.updateUserError', { ns: 'settings' }), 'danger')
    }
  }

  async resendInvitation(id: string): Promise<void> {
    try {
      const result = await invitationsApi.resendInvitation(id)
      if (result) {
        this.reactTo.lastIssued = result
        this.reactTo.invitations = this.invitations.map(i =>
          i.id === id ? result.invitation : i,
        )
        showToast(i18n.t('usersTeams.toast.invitationResent', { ns: 'settings' }), 'success')
      }
    } catch {
      showToast(i18n.t('usersTeams.toast.invitationResendError', { ns: 'settings' }), 'danger')
    }
  }

  async revokeInvitation(id: string): Promise<void> {
    try {
      const result = await invitationsApi.revokeInvitation(id)
      if (result) {
        this.reactTo.invitations = this.invitations.map(i =>
          i.id === id ? result : i,
        )
        showToast(i18n.t('usersTeams.toast.invitationRevoked', { ns: 'settings' }), 'success')
      }
    } catch {
      showToast(i18n.t('usersTeams.toast.invitationRevokeError', { ns: 'settings' }), 'danger')
    }
  }

  async deleteTeam(id: string): Promise<void> {
    try {
      await teamsApi.deleteTeam(id)
      this.reactTo.teams = this.teams.filter(t => t.id !== id)
    } catch {
      showToast(i18n.t('usersTeams.deleteError', { ns: 'settings' }), 'danger')
    }
  }

  /** Reloads all data from the backend. */
  async reload(): Promise<void> {
    await this._load()
  }

  async reloadUsers(): Promise<void> {
    try {
      this.reactTo.users = await usersApi.listUsers()
    } catch { /* ignore reload failure */ }
  }

  /** Called after a successful invitation creation — holds the issued invite and refreshes the list. */
  async onInvitationCreated(issue: CompanyInvitationIssue): Promise<void> {
    this.reactTo.lastIssued = issue
    try {
      this.reactTo.invitations = await invitationsApi.listInvitations()
    } catch { /* ignore reload failure */ }
  }

  /** Dismisses the share banner. */
  dismissLastIssued(): void {
    this.reactTo.lastIssued = null
  }

  /** Copies the full accept-invitation link (no email is sent, so the inviter shares it manually). */
  copyInviteLink(token: string): void {
    void navigator.clipboard.writeText(buildInvitationAcceptUrl(token))
    showToast(i18n.t('usersTeams.toast.tokenCopied', { ns: 'settings' }), 'success')
  }

  openUserModal(id: string | null = null): void {
    this.reactTo.userModalId = id
    this.reactTo.userModalOpen = true
  }

  closeUserModal(): void {
    this.reactTo.userModalOpen = false
    this.reactTo.userModalId = null
  }

  openInviteModal(): void {
    this.reactTo.inviteModalOpen = true
  }

  closeInviteModal(): void {
    this.reactTo.inviteModalOpen = false
  }

  openTeamModal(id: string | null = null): void {
    this.reactTo.teamModalId = id
    this.reactTo.teamModalOpen = true
  }

  closeTeamModal(): void {
    this.reactTo.teamModalOpen = false
    this.reactTo.teamModalId = null
  }

  openRoleDrawer(id: string | null = null): void {
    this.reactTo.roleDrawerId = id
    this.reactTo.roleDrawerOpen = true
  }

  closeRoleDrawer(): void {
    this.reactTo.roleDrawerOpen = false
    this.reactTo.roleDrawerId = null
  }
}

/** Users & Teams settings page with tabbed layout; `initialTab` opens on that tab when the user may see it. */
export function UsersTeamsPage({ initialTab }: { initialTab?: PageTab }) {
  const { t } = useTranslation('settings')
  const state = useCustomState(() => new UsersTeamsPageState(initialTab))
  const apiUsers = useComponentState(ApiUsersState)
  const accessRequests = useComponentState(AccessRequestsState)
  const userState = useUserState()

  // The role editor lives behind the Permissions View grant, so the tab only shows to grantees.
  const canViewRoles = userState.hasPermission(PermissionService.PERMISSIONS, PermissionAction.READ)
  const canViewCosts = userState.hasPermission(PermissionService.COSTS, PermissionAction.READ)
  const canAddUser = userState.hasPermission(PermissionService.AUTH_USERS, PermissionAction.CREATE)
  const canInvite = userState.hasPermission(PermissionService.AUTH_USERS, AuthUserLimited.INVITE)
  const canViewApiUsers = userState.hasPermission(PermissionService.AUTH_USERS, AuthUserElevated.ROTATE_API_USER_SECRETS)
  const canViewAccessRequests = userState.hasPermission(PermissionService.AUTH_USERS, PermissionAction.UPDATE)

  const tabs = [
    { key: 'users' as const, label: t('usersTeams.tabs.users') },
    ...(canViewApiUsers ? [{ key: 'apiUsers' as const, label: t('usersTeams.tabs.apiUsers') }] : []),
    ...(canInvite ? [{ key: 'invitations' as const, label: t('usersTeams.tabs.invitations') }] : []),
    ...(canViewAccessRequests ? [{ key: 'accessRequests' as const, label: t('usersTeams.tabs.accessRequests') }] : []),
    { key: 'teams' as const, label: t('usersTeams.tabs.teams') },
    ...(canViewRoles ? [{ key: 'roles' as const, label: t('usersTeams.tabs.roles') }] : []),
  ]
  // A tab named by the URL but hidden from this user falls back to Users.
  const activeTab = tabs.some(tab => tab.key === state.activeTab) ? state.activeTab : 'users'

  const searchPlaceholder = activeTab === 'users'
    ? t('shared.fields.searchUsers')
    : activeTab === 'apiUsers'
      ? t('usersTeams.search.apiUsers')
      : activeTab === 'invitations'
        ? t('usersTeams.search.invitations')
        : activeTab === 'accessRequests'
          ? t('usersTeams.search.accessRequests')
          : activeTab === 'teams'
            ? t('usersTeams.search.teams')
            : t('usersTeams.search.roles')

  const primaryAction = activeTab === 'users'
    ? (canAddUser ? { label: t('shared.fields.addUser'), onClick: () => state.openUserModal() } : null)
    : activeTab === 'apiUsers'
      ? (userState.isSuperAdmin ? { label: t('usersTeams.actions.addApiUser'), onClick: () => apiUsers.openCreate() } : null)
      : activeTab === 'invitations'
        ? { label: t('shared.fields.inviteUser'), onClick: () => state.openInviteModal() }
        : activeTab === 'accessRequests'
          ? null
          : activeTab === 'teams'
            ? { label: t('usersTeams.actions.addTeam'), onClick: () => state.openTeamModal() }
            : { label: t('usersTeams.actions.addRole'), onClick: () => state.openRoleDrawer() }

  return (
    <div className="p-4 overflow-y-auto h-100">
      {/* Header */}
      <h4 className="fw-bold mb-1">{t('shared.fields.usersTeams')}</h4>
      <p className="text-secondary mb-4">{t('usersTeams.subtitle')}</p>

      {state.error && (
        <div className="alert alert-danger" role="alert">
          {t('usersTeams.loadError')}
        </div>
      )}

      {/* Tabs */}
      <Tabs
        tabs={tabs}
        activeKey={activeTab}
        onChange={key => { state.searchQuery = ''; state.activeTab = key as PageTab }}
        variant="underline"
        size="sm"
        className="mb-3"
      />

      {/* Top bar: search left, primary action right */}
      <div className="ynt-inline-icon-row-mb">
        <div className="position-relative flex-grow-1" style={{ maxWidth: '24rem' }}>
          <Search className="position-absolute top-50 translate-middle-y text-secondary" size={16} style={{ left: '12px' }} />
          <input
            type="text"
            className="form-control"
            style={{ paddingLeft: '2rem', paddingRight: '2.0625rem' }}
            placeholder={searchPlaceholder}
            value={state.searchQuery}
            onChange={e => { state.searchQuery = e.target.value }}
          />
          {state.searchQuery && (
            <button
              type="button"
              className="btn btn-sm border-0 p-0 position-absolute top-50 translate-middle-y"
              style={{ right: '12px' }}
              onClick={() => { state.searchQuery = '' }}
            >
              <X size={14} className="text-secondary" />
            </button>
          )}
        </div>
        <div className="ms-auto d-flex align-items-center gap-2">
          {primaryAction && (
            <button type="button" className="btn btn-primary ynt-inline-action-btn" onClick={primaryAction.onClick}>
              <Plus size={16} />
              {primaryAction.label}
            </button>
          )}
        </div>
      </div>

      {/* Tab content */}
      {state.loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : activeTab === 'users' ? (
        <UsersTab
          users={state.filteredUsers}
          totalCount={state.users.length}
          roles={state.roles}
          showRates={canViewCosts}
          userRates={state.userCostRates}
          defaultRate={state.defaultCostRate}
          onEdit={id => state.openUserModal(id)}
          onDelete={id => state.requestDeleteUser(id)}
          onToggleEnabled={(id, enabled) => void state.toggleEnabled(id, enabled)}
        />
      ) : activeTab === 'apiUsers' ? (
        <ApiUsersTab state={apiUsers} searchQuery={state.searchQuery} roles={state.roles} />
      ) : activeTab === 'invitations' ? (
        <InvitationsTab
          invitations={state.filteredInvitations}
          totalCount={state.invitations.length}
          onResend={id => void state.resendInvitation(id)}
          onRevoke={id => state.requestRevokeInvitation(id)}
          onCopyLink={token => state.copyInviteLink(token)}
          lastIssued={state.lastIssued}
          onDismissLastIssued={() => state.dismissLastIssued()}
        />
      ) : activeTab === 'accessRequests' ? (
        <AccessRequestsTab
          state={accessRequests}
          searchQuery={state.searchQuery}
          users={state.users}
          roles={state.roles}
          onApproved={() => void state.reloadUsers()}
        />
      ) : activeTab === 'teams' ? (
        <TeamsTab
          teams={state.filteredTeams}
          totalCount={state.teams.length}
          users={state.users}
          onEdit={id => state.openTeamModal(id)}
          onDelete={id => state.requestDeleteTeam(id)}
        />
      ) : (
        <RolesTab
          roles={state.filteredRoles}
          totalCount={state.roles.length}
          users={state.users}
          onEdit={id => state.openRoleDrawer(id)}
          onDelete={id => state.requestDeleteRole(id)}
        />
      )}

      {/* Modals */}
      <UserEditModal
        isOpen={state.userModalOpen}
        userId={state.userModalId}
        roles={state.roles}
        onClose={() => state.closeUserModal()}
        onSaved={() => void state.reload()}
      />
      <InviteUserModal
        isOpen={state.inviteModalOpen}
        roles={state.roles}
        onClose={() => state.closeInviteModal()}
        onSaved={issue => void state.onInvitationCreated(issue)}
      />
      <TeamEditModal
        isOpen={state.teamModalOpen}
        teamId={state.teamModalId}
        users={state.users}
        onClose={() => state.closeTeamModal()}
        onSaved={() => void state.reload()}
      />
      <RoleEditDrawer
        open={state.roleDrawerOpen}
        roleId={state.roleDrawerId}
        roles={state.roles}
        onClose={() => state.closeRoleDrawer()}
        onSaved={() => { state.closeRoleDrawer(); void state.reload() }}
      />

      {/* Destructive action confirmation */}
      {(() => {
        const action = state.confirmAction
        const base = action ? confirmKeyMap[action.type] : null
        return (
          <ConfirmModal
            isOpen={action !== null}
            title={base ? t(`${base}.title`) : ''}
            // Message keys carry inline markup (e.g. the bolded name), so render via
            // dangerouslySetInnerHTML — the primitive's `message` accepts a ReactNode.
            message={base ? <span dangerouslySetInnerHTML={{ __html: t(`${base}.message`, { name: action?.name ?? '' }) }} /> : null}
            confirmLabel={action?.type === 'revokeInvitation' ? t('shared.fields.revoke') : t('shared.actions.delete')}
            cancelLabel={t('shared.actions.cancel')}
            genericErrorLabel={t('usersTeams.deleteError')}
            variant="danger"
            onClose={() => state.cancelConfirmAction()}
            onConfirm={() => state.executeConfirmedAction()}
          />
        )
      })()}
    </div>
  )
}
